import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';

interface DataExplainerProps {
  title: string;
  explanation: string;
  className?: string;
}

export const DataExplainer: React.FC<DataExplainerProps> = ({ 
  title, 
  explanation, 
  className = "" 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={(e) => {
          e.preventDefault();
          setShowTooltip(!showTooltip);
        }}
        className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label={`Explanation for ${title}`}
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      
      {showTooltip && (
        <div className="absolute z-50 w-80 p-3 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 left-0 transform">
          <div className="text-sm">
            <div className="font-semibold text-gray-900 mb-1">{title}</div>
            <div className="text-gray-600 leading-relaxed">{explanation}</div>
          </div>
          {/* Arrow */}
          <div className="absolute -top-1 left-4 w-2 h-2 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
        </div>
      )}
    </div>
  );
};

// Pre-defined explainers for common metrics
export const DATA_EXPLAINERS = {
  launchReady: {
    title: "Launch Ready Rate",
    explanation: "Percentage of creatives that scored 80+ points and are ready to launch. These creatives have minimal compliance issues and strong performance indicators."
  },
  overallScore: {
    title: "Overall Score", 
    explanation: "Combined score (0-100) based on compliance across all platforms and performance metrics. Higher scores indicate better creative quality."
  },
  complianceScore: {
    title: "Compliance Score",
    explanation: "Platform-specific score (0-100) measuring how well the creative follows that platform's advertising policies and guidelines."
  },
  performanceScore: {
    title: "Performance Score", 
    explanation: "Predicted performance rating (0-100) based on creative elements like visual appeal, text clarity, and engagement potential."
  },
  platformPerformance: {
    title: "Platform Performance",
    explanation: "Shows average performance scores for each advertising platform. Different platforms have varying requirements, so scores can differ significantly."
  },
  creditsUsed: {
    title: "Credits Used",
    explanation: "Each creative analysis consumes 1 credit. Credits reset monthly based on your subscription plan."
  },
  qualityTrend: {
    title: "Quality Trend",
    explanation: "Indicates whether your recent creatives are improving (↗) or declining (↘) compared to previous analyses."
  },
  topPerformers: {
    title: "Top Performers",
    explanation: "Creatives with the highest combined scores across compliance and performance metrics. Click to view detailed analysis."
  },
  highPerformers: {
    title: "High Performers",
    explanation: "Exceptional creatives with overall scores ≥90%. These represent your best work with outstanding compliance and performance across all platforms."
  },
  commonIssues: {
    title: "Common Issues",
    explanation: "Most frequent compliance problems found across your creatives. Address these patterns to improve overall quality."
  },
  monthlyTrends: {
    title: "Monthly Trends",
    explanation: "6-month view of analysis volume and approval rates. Helps track improvement over time and identify patterns."
  }
}; 